import { Component, OnInit, OnDestroy, ElementRef, HostListener, ChangeDetectorRef, ViewChild, AfterViewInit } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { TamDashboardActions, TamDashboardEvents } from '../tam-dashboard.model';
import { filter } from 'rxjs/operators';
import { TamDashboardTileBaseComponent } from '../tam-dashboard-tile.base.component';
import { ChartComponent } from '@progress/kendo-angular-charts';
import { TamDashboardLineChartTileConfiguration } from './tam-dashboard-line-chart-tile.config';

@Component({
    selector: 'tam-dashboard-line-chart-tile',
    templateUrl: './tam-dashboard-line-chart-tile.component.html',
    styleUrls: ['./tam-dashboard-line-chart-tile.component.scss']
})
export class TamDashboardLineChartTileComponent implements OnInit, AfterViewInit, OnDestroy, TamDashboardTileBaseComponent {
    @ViewChild('chart', { static: false }) private _chart: ChartComponent;
    @ViewChild('container', { static: false }) private _container: ElementRef;
    data: any;
    series: any[];
    seriesColors = ['#63b9bf', '#e6be0a', '#da8945', '#bdd344', '#cc2f64', '#d248bd', '#784f9b'];
    categoryMax: any = null;
    categoryMin: any = null;
    categoryAxisItems: any[] = [];
    label: string;
    chartWidth = 300;
    chartLabelFormat: string;
    chartToolTipFormat: string;
    config: TamDashboardLineChartTileConfiguration;

    private _afterViewFeedback$ = new Subject<string>();
    private _matchPrint: MediaQueryList = window.matchMedia('print');
    private _destroySubscriptions: Array<Subscription> = [];
    private _widthCalculator: (containerWidth: number) => number = (containerWidth) => containerWidth || this.chartWidth;

    constructor(
        private el: ElementRef,
        private _cdr: ChangeDetectorRef,
    ) { }

    ngOnInit() {
        // safari not support addEventListener, support addListener
        if (this._matchPrint) {
            if (this._matchPrint.addEventListener) {
                this._matchPrint.addEventListener('change', this._resizeChart);
            } else if (this._matchPrint['addListener']) {
                this._matchPrint['addListener'](this._resizeChart);
            }
        }
    }

    ngAfterViewInit(): void {
        this._afterViewFeedback$.next(TamDashboardEvents.TILE_AFTER_VIEW_INIT);
    }

    ngOnDestroy() {
        if (this._matchPrint) {
            if (this._matchPrint.removeEventListener) {
                this._matchPrint.removeEventListener('change', this._resizeChart);
            } else if (this._matchPrint['addListener']) {
                this._matchPrint['removeListener'](this._resizeChart);
            }
            this._resizeChart = null;
        }
        this._destroySubscriptions.forEach(element => element.unsubscribe());
        if (this.config) {
            this.config.destroy();
        }
    }


    labelContent = (e: any) => {
        if (this.config && this.config.labelContent) {
            return this.config.labelContent(e);
        }
        return e.value;
    };

    onSeriesClick(event) {
        if (this.config && this.config.onSeriesClick) {
            this.config.onSeriesClick(event);
        }
    }

    setConfig(config: TamDashboardLineChartTileConfiguration): boolean {
        // modify reason, same as pie chart
        this.config = config;
        this._init();
        return true;
    }

    getStyle() {
        if (this.config && this.config.domLayout === 'print') {
            return {
                'min-width': this.chartWidth + 'px',
            };
        }
        return {
            'width': this.chartWidth + 'px',
        };
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this._recalculateWidth();
    }

    @HostListener('window:beforeprint', ['$event'])
    onBeforePrint() {
        // When printing, min-width affects the chart.resize,
        // So when the chart width does not appear scroll bar, set the min-width value to auto
        const containerWidth = this._container.nativeElement.clientWidth;
        if (containerWidth === this.chartWidth) {
            this._chart['element'].nativeElement.style.minWidth = 'auto';
        }
    }

    trackByFn(index) {
        return index;
    }

    private _init() {
        this._destroySubscriptions.forEach(element => element.unsubscribe());
        this._destroySubscriptions.push(
            this.config.actionSubject$.pipe(
                filter((action: any) => action && action.id === this.config.id),
            ).subscribe(action => this._onSubscribeActions(action)),
            this._afterViewFeedback$.subscribe((payload) => {
                if (payload === TamDashboardEvents.TILE_AFTER_VIEW_INIT) {
                    this.config.feedbackSubject$.next({
                        type: TamDashboardEvents.TILE_AFTER_VIEW_INIT,
                        id: this.config.id
                    });
                }
            })
        );
    }

    private _resizeChart = (mql) => {
        if (mql.matches) {
            this._chart.resize();
        }
    };

    private _onSubscribeActions(action) {
        switch (action.type) {
            case TamDashboardActions.SET_TILE_DATA: {
                if (!action.payload || !action.payload.data) {
                    break;
                }
                this.data = action.payload.data;
                this.categoryAxisItems = action.payload.categoryAxisItems;
                this.chartLabelFormat = action.payload.chartLabelFormat;
                this.chartToolTipFormat = action.payload.chartToolTipFormat;
                this.series = action.payload.series;

                this._widthCalculator = action.payload.widthCalculator;
                this._recalculateWidth(true);
                break;
            }
        }
    }

    private _recalculateWidth(markSetData = false) {
        setTimeout(() => {
            const containerWidth = (this.el.nativeElement.firstChild ?
                this.el.nativeElement.firstChild.clientWidth : this.el.nativeElement.clientWidth) || this.chartWidth;
            this.chartWidth = this._widthCalculator(containerWidth);
            if (this._cdr && !this._cdr['destroyed']) {
                this._cdr.detectChanges();
            }
            if (markSetData) {
                this.config.feedbackSubject$.next({
                    type: TamDashboardEvents.TILE_LOADED_DATA,
                    id: this.config.id
                });
            }
        }, 250);
    }
}
